import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useLang } from "../../context/LangContext";
import { SHOWREEL } from "../../lib/i18n";
import { Reveal, SplitReveal } from "./Motion";

/* ---------- Marquee ---------- */
export const Marquee = () => {
  const { t } = useLang();
  const items = [...t.marquee, ...t.marquee];
  return (
    <div data-testid="marquee-section" className="relative overflow-hidden border-y border-[#d4af37]/20 bg-[#0a0a0b] py-6 text-[#fafaf7]">
      <div className="flex w-max animate-[marquee_38s_linear_infinite] gap-14 whitespace-nowrap font-display text-2xl font-medium tracking-tight md:text-4xl">
        {items.map((w, i) => (
          <span key={i} className="flex items-center gap-14">
            <span className={i % 2 ? "font-serif-i gold-text font-normal" : "text-white/85"}>{w}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" />
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- Stats ---------- */
const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / 1800, 1);
      setN(Math.round((1 - Math.pow(1 - p, 3)) * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return <span ref={ref} className="font-display text-6xl font-light tabular-nums gold-text md:text-8xl">{n}{suffix}</span>;
};

export const Stats = () => {
  const { t } = useLang();
  return (
    <section data-testid="stats-section" className="bg-[#fafaf7] py-16 text-[#0a0a0b] md:py-24">
      <div className="wrap grid gap-12 md:grid-cols-4 md:gap-8">
        {t.stats.items.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1}>
            <div className="border-t border-black/10 pt-6" data-testid={`stat-${i}`}>
              <Counter to={s.v} suffix={s.suffix} />
              <p className="mt-4 max-w-[260px] text-sm leading-relaxed text-black/70">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ---------- Showreel (horizontal scroll) ---------- */
export const Showreel = () => {
  const { t } = useLang();
  const r = t.showreel;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-62%"]);
  const line = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  return (
    <section ref={ref} data-testid="showreel-section" className="relative h-[220vh] bg-[#0a0a0b] text-[#fafaf7]">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="wrap mb-10 flex items-end justify-between">
          <div>
            <p className="eyebrow mb-5">{r.eyebrow}</p>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-4xl lg:text-5xl"><SplitReveal key={r.title} text={r.title} /></h2>
            <p className="mt-3 max-w-xl text-xs text-white/60 md:text-sm" data-testid="showreel-note">{r.note}</p>
          </div>
          <span className="hidden font-mono text-[11px] uppercase tracking-[0.3em] text-white/40 md:inline">{r.hint} →</span>
        </div>
        <motion.div style={{ x }} className="flex w-max gap-6 pl-6 md:gap-10 md:pl-14">
          {SHOWREEL.map((src, i) => (
            <figure key={src} className={`group relative shrink-0 overflow-hidden rounded-[28px] ${i % 3 === 1 ? "h-[34vh] w-[30vw] min-w-[240px]" : "h-[42vh] w-[38vw] min-w-[280px]"}`} data-testid={`showreel-item-${i}`}>
              <img src={src} alt={r.captions[i]} className="h-full w-full object-cover transition-transform [transition-duration:2000ms] group-hover:scale-110" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b]/80 via-transparent to-transparent" />
              <figcaption className="absolute bottom-6 left-6 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-white/80">
                <span className="text-[#e8c979]">0{i + 1}</span>{r.captions[i]}
              </figcaption>
            </figure>
          ))}
        </motion.div>
        <div className="wrap mt-10"><div className="h-px w-full bg-white/10"><motion.div style={{ width: line }} className="h-px bg-[#d4af37]" /></div></div>
      </div>
    </section>
  );
};
